import { ActionIcon, Card, Group, Menu, Table, Text } from '@mantine/core';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { DataObject } from '@/api/objects';
import { ButtonLink } from '@/components/links';
import { CellText } from '@/components/CellText';
import { NAME_MAX_WIDTH, NAME_MIN_WIDTH } from '@/components/tableWidths';
import { StatusBadge } from '@/components/StatusBadge';
import { rowClick, rowStyle } from '@/components/rowClick';
import { SortableTh } from '@/components/SortableTh';
import { useTableSort } from '@/components/useTableSort';
import { useFormat } from '@/components/useFormat';
import { useKindLabel } from '@/features/objects/useKindLabel';
import { brand } from '@/theme/theme';
import { CommentCount } from './InternalComments';
import { InternalStateChip } from './InternalStateChip';

export interface StaffObjectsTableProps {
  objects: DataObject[];
  onDelete: (o: DataObject) => void;
  onDeleteRaw: (o: DataObject) => void;
  /** Internal comments per object id (P16). */
  commentCounts?: Record<string, number>;
}

/** A live, ready boundary the viewer can draw: its listing holds exactly one GeoPackage, as the backend requires. */
function viewableBoundary(o: DataObject): boolean {
  if (o.kind !== 'boundary' || o.state !== 'ready' || o.deleted) return false;
  const files = (o.listing_snapshot as { files?: { path: string }[] } | null | undefined)?.files ?? [];
  return files.filter((f) => f.path.toLowerCase().endsWith('.gpkg')).length === 1;
}

/** Every column staff need (P9): internal state, client badge, size, flags, actions. */
export function StaffObjectsTable({ objects, onDelete, onDeleteRaw, commentCounts }: StaffObjectsTableProps) {
  const { t } = useTranslation('projects');
  const { bytes } = useFormat();
  const kindLabel = useKindLabel();
  const navigate = useNavigate();
  const { sorted, sortProps } = useTableSort(objects, {
    name: (o) => o.name || o.declared_name,
    kind: (o) => kindLabel(o.kind),
    state: (o) => o.state,
    status: (o) => t(`status.object.${o.client_status}`, { defaultValue: o.client_status }),
    size: (o) => o.total_bytes,
    author: (o) => o.uploaded_by ? o.uploaded_by.full_name || o.uploaded_by.email : null,
    flags: (o) => [o.deleted && 'deleted', o.raw_deleted && 'raw_deleted', o.is_deliverable && 'deliverable', o.internal && 'internal'].filter(Boolean).join(', '),
  });
  return (
    <Card p={0} style={{ overflowX: 'auto' }}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <SortableTh {...sortProps('name')}>{t('objects.name')}</SortableTh>
            <SortableTh {...sortProps('kind')}>{t('objects.kind')}</SortableTh>
            <SortableTh {...sortProps('state')}>{t('project.state')}</SortableTh>
            <SortableTh {...sortProps('status')}>{t('staff.clientStatus')}</SortableTh>
            <SortableTh {...sortProps('size')}>{t('objects.size')}</SortableTh>
            <SortableTh {...sortProps('author')}>{t('objects.uploadedBy')}</SortableTh>
            <SortableTh {...sortProps('flags')}>{t('staff.flags')}</SortableTh>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sorted.map((o) => (
            <Table.Tr
              key={o.public_id}
              style={{ ...rowStyle, ...(o.deleted ? { opacity: 0.6 } : {}) }}
              onClick={rowClick(() => void navigate({ to: '/staff/objects/$objectId', params: { objectId: o.public_id } }))}
            >
              <Table.Td>
                <Group gap={6} wrap="nowrap" align="flex-start">
                  <CellText lines={2} fz={14} fw={600} miw={NAME_MIN_WIDTH} maw={NAME_MAX_WIDTH} title={o.name || o.declared_name || undefined}>
                    <Link to="/staff/objects/$objectId" params={{ objectId: o.public_id }} style={{ color: brand.text, textDecoration: 'none' }}>
                      {o.name || o.declared_name || '—'}
                    </Link>
                  </CellText>
                  <CommentCount count={commentCounts?.[o.public_id]} />
                </Group>
              </Table.Td>
              <Table.Td>{kindLabel(o.kind)}</Table.Td>
              <Table.Td>
                <InternalStateChip state={o.state ?? ''} />
              </Table.Td>
              <Table.Td>
                <StatusBadge status={o.client_status} scope="object" />
              </Table.Td>
              <Table.Td style={{ whiteSpace: 'nowrap' }}>{o.total_bytes ? bytes(o.total_bytes) : '—'}</Table.Td>
              <Table.Td>{o.uploaded_by ? o.uploaded_by.full_name || (o.uploaded_by.email ?? '—') : '—'}</Table.Td>
              <Table.Td>
                <Text fz={12} c={brand.textMuted}>
                  {[o.deleted && 'deleted', o.raw_deleted && 'raw_deleted', o.is_deliverable && 'deliverable', o.internal && 'internal'].filter(Boolean).join(', ') || '—'}
                </Text>
              </Table.Td>
              <Table.Td>
                <Group gap={4} wrap="nowrap" justify="flex-end">
                  {viewableBoundary(o) && (
                    <ButtonLink size="compact-xs" variant="light" to="/staff/projects/$projectId/boundary/$objectId" params={{ projectId: o.project, objectId: o.public_id }}>
                      {t('staff.viewBoundary')}
                    </ButtonLink>
                  )}
                  {!o.deleted && (
                    // The destructive actions sit behind one menu, so a row is not a wall of red buttons.
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" size="sm" aria-label={t('staff.objectActions')} onClick={(e) => e.stopPropagation()}>
                          ⋯
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                        <Menu.Item color="red" onClick={() => onDelete(o)}>
                          {t('delete.action')}
                        </Menu.Item>
                        {!o.raw_deleted && o.processed_uri && (
                          <Menu.Item color="red" onClick={() => onDeleteRaw(o)}>
                            {t('staff.deleteRaw')}
                          </Menu.Item>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}
